import React, { useMemo, useState } from 'react';
import { Search, X, Users, RefreshCw } from 'lucide-react';
import {
  ALL_ASSIGNABLE_ROLES,
  PLAN_TIERS,
  PLAN_TIER_LABELS,
  ROLE_LABELS,
  SUBSCRIPTION_LABELS,
  SUBSCRIPTION_STATUS,
  resolvePlanTier,
} from '../../constants/roles';

/**
 * Resolve a list of user records to the set of UIDs that match the supplied
 * segment filters PLUS any individually-picked UIDs. This is the snapshot
 * we persist on the announcement (`target.uids`) so the Firestore query
 * stays simple and the security rule is statically provable.
 *
 * Empty segments => keep manual UIDs only. ANY filter set => intersect.
 */
export function resolveTargetUids({ users, manualUids, segments }) {
  const setLike = new Set(Array.isArray(manualUids) ? manualUids : []);
  const hasSegmentFilters = (
    (segments?.roles?.length || 0) > 0
    || (segments?.planTiers?.length || 0) > 0
    || (segments?.subscriptionStatuses?.length || 0) > 0
    || segments?.hasFarms === true
    || segments?.hasFarms === false
  );
  if (!hasSegmentFilters) return Array.from(setLike);

  for (const u of users) {
    if (!u?.id) continue;
    if (segments.roles?.length && !segments.roles.includes(u.role || 'user')) continue;
    if (segments.planTiers?.length && !segments.planTiers.includes(resolvePlanTier(u))) continue;
    if (segments.subscriptionStatuses?.length) {
      const s = u.subscriptionStatus || SUBSCRIPTION_STATUS.NONE;
      if (!segments.subscriptionStatuses.includes(s)) continue;
    }
    if (segments.hasFarms === true && (u.farmIds || []).length === 0) continue;
    if (segments.hasFarms === false && (u.farmIds || []).length > 0) continue;
    setLike.add(u.id);
  }
  return Array.from(setLike);
}

function ChipMulti({ label, options, selected, onChange }) {
  const has = (v) => selected.includes(v);
  const toggle = (v) => {
    if (has(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">
        {label}
      </span>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => toggle(opt.value)}
          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
            has(opt.value)
              ? 'bg-[#043132] border-[#043132] text-white'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
          }`}
        >
          {opt.label}
        </button>
      ))}
      {selected.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange([])}
          className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
        >
          <X size={11} /> Clear
        </button>
      ) : null}
    </div>
  );
}

/**
 * Audience picker for AnnouncementsAdmin. Owns the editor's draft target
 * shape but does not persist anything itself — caller controls saving.
 */
export default function UserTargetPicker({ value, onChange, users }) {
  const target = value || { mode: 'all', uids: [], segments: { roles: [], planTiers: [], subscriptionStatuses: [], hasFarms: null } };
  const [search, setSearch] = useState('');

  const usersById = useMemo(() => {
    const m = new Map();
    (users || []).forEach((u) => { if (u?.id) m.set(u.id, u); });
    return m;
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter((u) => (
      (u.email || '').toLowerCase().includes(q) ||
      (u.businessName || '').toLowerCase().includes(q) ||
      (u.displayName || '').toLowerCase().includes(q) ||
      u.id.toLowerCase().includes(q)
    ));
  }, [users, search]);

  const setMode = (mode) => {
    onChange({ ...target, mode });
  };

  const setSegments = (next) => {
    onChange({ ...target, segments: { ...target.segments, ...next } });
  };

  const toggleUid = (uid) => {
    const cur = new Set(target.uids || []);
    if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
    onChange({ ...target, uids: Array.from(cur) });
  };

  const removeUid = (uid) => {
    onChange({ ...target, uids: (target.uids || []).filter((u) => u !== uid) });
  };

  const refreshRecipients = () => {
    const resolved = resolveTargetUids({
      users: users || [],
      manualUids: target.uids || [],
      segments: target.segments || {},
    });
    onChange({ ...target, uids: resolved });
  };

  const previewCount = useMemo(() => {
    if (target.mode === 'all') return (users || []).length;
    return resolveTargetUids({
      users: users || [],
      manualUids: target.uids || [],
      segments: target.segments || {},
    }).length;
  }, [target, users]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            checked={target.mode === 'all'}
            onChange={() => setMode('all')}
            className="text-[#043132] focus:ring-[#043132]"
          />
          <span className="font-medium text-slate-700">All signed-in users</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            checked={target.mode === 'selected'}
            onChange={() => setMode('selected')}
            className="text-[#043132] focus:ring-[#043132]"
          />
          <span className="font-medium text-slate-700">Selected users</span>
        </label>
        <span className="ml-auto text-[11px] text-slate-500 inline-flex items-center gap-1">
          <Users size={12} /> {previewCount} recipient{previewCount === 1 ? '' : 's'}
        </span>
      </div>

      {target.mode === 'selected' ? (
        <div className="space-y-3 border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <p className="text-[11px] text-slate-500">
            Combine segment chips with individual picks. We resolve all of these to a UID snapshot when you save.
            New users joining later will not retroactively see this announcement until you click <strong>Refresh recipients</strong>.
          </p>

          <div className="space-y-2">
            <ChipMulti
              label="Roles"
              options={ALL_ASSIGNABLE_ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
              selected={target.segments?.roles || []}
              onChange={(roles) => setSegments({ roles })}
            />
            <ChipMulti
              label="Plan tier"
              options={Object.values(PLAN_TIERS).map((t) => ({ value: t, label: PLAN_TIER_LABELS[t] }))}
              selected={target.segments?.planTiers || []}
              onChange={(planTiers) => setSegments({ planTiers })}
            />
            <ChipMulti
              label="Subscription"
              options={Object.values(SUBSCRIPTION_STATUS).map((s) => ({ value: s, label: SUBSCRIPTION_LABELS[s] }))}
              selected={target.segments?.subscriptionStatuses || []}
              onChange={(subscriptionStatuses) => setSegments({ subscriptionStatuses })}
            />
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mr-1">
                Property assignments
              </span>
              {[
                { value: null, label: 'Any' },
                { value: true, label: 'Has properties' },
                { value: false, label: 'No properties' },
              ].map((opt) => {
                const active = target.segments?.hasFarms === opt.value;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => setSegments({ hasFarms: opt.value })}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-[#043132] border-[#043132] text-white'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-700">Individual users</span>
              <button
                type="button"
                onClick={refreshRecipients}
                className="text-[11px] text-[#043132] hover:underline inline-flex items-center gap-1"
                title="Resolve segment filters into the explicit UID list saved on this announcement"
              >
                <RefreshCw size={11} /> Refresh recipients
              </button>
            </div>

            {(target.uids || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(target.uids || []).map((uid) => {
                  const u = usersById.get(uid);
                  const label = u ? (u.email || u.businessName || u.displayName || uid) : uid;
                  return (
                    <span key={uid} className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-200 rounded-full px-2 py-0.5">
                      <span className="truncate max-w-[200px]" title={uid}>{label}</span>
                      <button
                        type="button"
                        onClick={() => removeUid(uid)}
                        className="text-slate-400 hover:text-rose-600"
                        aria-label="Remove recipient"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : null}

            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users by email, business, or UID…"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none bg-white"
              />
            </div>

            <div className="border border-slate-200 rounded-lg bg-white max-h-60 overflow-y-auto divide-y divide-slate-50">
              {filteredUsers.slice(0, 200).map((u) => {
                const checked = (target.uids || []).includes(u.id);
                return (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUid(u.id)}
                      className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-800 truncate">{u.email || u.id}</div>
                      <div className="text-[10px] text-slate-400 truncate">
                        {[u.businessName, ROLE_LABELS[u.role], PLAN_TIER_LABELS[resolvePlanTier(u)]].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </label>
                );
              })}
              {filteredUsers.length > 200 ? (
                <div className="px-3 py-2 text-[11px] text-slate-400 italic">
                  Showing first 200 matches. Refine the search to narrow further.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
